import { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { DirectLine, ConnectionStatus } from 'botframework-directlinejs';
import { parseDirectLineAiResponse, notifySessionTimeout } from '../api/chatApi';
import { setEscalationRequired, setSessionEnded } from '../redux/chatSlice';
import { uploadAttachment } from '../api/sfdcApi';
import { formatWaitTime } from '../utils/helpers';
import { getLiveAgentMessage, getLiveAgentName } from '../utils/liveAgentActivities';
import { LOCAL_SESSION_KEY, LOCAL_UID_KEY } from '../utils/sessionKeys';
import { getAgentAttachment, logAgentAvailability, getDirectLineChannelData, appendAiReply } from '../utils/directLineHelpers';
import { BACKEND_BASE_URL } from '../api/config';

const LIVE_AGENT_FAILURE_MESSAGE = 'Unable to reach a live agent right now. Please try again.';
const LIVE_IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const LIVE_IDLE_CONFIRM_MS = 30 * 1000;
const LIVE_ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

// Owns the DirectLine connection, live-agent handoff state and message handling.
export function useDirectLine({
  orderLoading,
  order,
  userName,
  userNameLoading,
  liveAgentConnectivity,
  escalationRequired,
  clearEscalation,
  clearLiveAgentConnectivity,
  sessionTimedOut,
}) {
  const dispatch = useDispatch();

  const [isLiveMode, setIsLiveMode]   = useState(false);
  const [dlMessages, setDlMessages]   = useState([]);
  const [directLineLoading, setDirectLineLoading] = useState(false);
  const [, setDirectLineStreamingEvents] = useState([]);
  const [dlOnline, setDlOnline] = useState(false);
  const [dlHandoff, setDlHandoff]     = useState(false);
  const [, setDlStatus]               = useState('');
  const [dlFailed, setDlFailed]       = useState(false);
  const [, setLiveAgentName]          = useState('');
  // True while the user is waiting in the InContact queue (shows the QueueCard).
  const [dlQueueActive, setDlQueueActive] = useState(false);
  const [dlWaitTime, setDlWaitTime]   = useState('');
  // True while waiting for the backend's disconnectLiveAgent ack after Exit Chat/queue.
  const [dlExiting, setDlExiting]     = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [liveIdlePrompt, setLiveIdlePrompt] = useState(false);
  const [liveIdleResetKey, setLiveIdleResetKey] = useState(0);
  const [liveAgentDiagnostics, setLiveAgentDiagnostics] = useState({
    connection: 'Not connected',
    accepted: 0,
    rejected: 0,
    lastEvent: null,
  });
  const directLineRef                 = useRef(null);
  const directLineLoadingRef = useRef(false);
  const responsePlaybackRef = useRef(null);
  const pendingAiReplyRef = useRef([]);
  const postSubscriptionRef = useRef(null);
  const activePostIdRef = useRef(null);
  const abortedPostIdsRef = useRef(new Set());
  const lastQuestionRef = useRef('');
  const metadataReplayCompleteRef = useRef(false);
  const aiReplyReceivedRef = useRef(false);
  const metadataEscalationRef = useRef(false);
  const tokenRequestRef = useRef(null);
  const liveModeRef = useRef(false);
  // Read by the DirectLine subscription closure, which is created once per connection.
  const userNameRef = useRef(userName);
  userNameRef.current = userName;
  const subscriptionRef               = useRef(null);
  const connectionStatusSubRef        = useRef(null);
  // connectToLiveAgent is memoized with an empty dep array, so its
  // dl.activity$ subscription closure would otherwise always see the stale
  // (initial) value of dlHandoff. Track it in a ref so handlers below can
  // read the up-to-date value.
  const dlHandoffRef                  = useRef(false);
  // Same staleness issue as dlHandoffRef, but for the resolved agent name.
  const liveAgentNameRef               = useRef('');
  // InContact identifies a queued chat by contactId. Preserve it so an exit
  // request can cancel the exact contact rather than only closing the browser
  // side of the DirectLine conversation.
  const queueContactIdRef              = useRef(null);
  // Set from incoming activities; handleAttachFile runs outside the subscription closure.
  const inContactSessionIdRef          = useRef(null);
  const recentEventMessagesRef = useRef(new Map());

  // 30-second deduplication window for event messages
  const shouldDisplayEventMessage = useCallback((content) => {
    const key = String(content || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!key) return true;

    const now = Date.now();
    const previousTime = recentEventMessagesRef.current.get(key);
    if (previousTime && now - previousTime < 30000) return false;

    recentEventMessagesRef.current.set(key, now);
    for (const [message, timestamp] of recentEventMessagesRef.current) {
      if (now - timestamp >= 30000) recentEventMessagesRef.current.delete(message);
    }
    while (recentEventMessagesRef.current.size > 100) {
      recentEventMessagesRef.current.delete(recentEventMessagesRef.current.keys().next().value);
    }
    return true;
  }, []);

  // ── Tear down the DirectLine connection (unsubscribe + close socket) ───────
  // Shared by the unmount cleanup and the user-triggered "leave queue" action.
  const teardownDirectLine = useCallback(() => {
    responsePlaybackRef.current?.abort();
    tokenRequestRef.current?.abort();
    tokenRequestRef.current = null;
    setDlOnline(false);
    subscriptionRef.current?.unsubscribe();
    connectionStatusSubRef.current?.unsubscribe();
    try {
      // DirectLine can already be ending (e.g. backend sent disconnectLiveAgent
      // and closed the socket) — .end() throws "conversation ended" in that case.
      directLineRef.current?.end();
    } catch (error) {
      console.warn('[DirectLine] end() threw during teardown (connection likely already closed):', error);
    }
    directLineRef.current = null;
    subscriptionRef.current = null;
    connectionStatusSubRef.current = null;
  }, []);

  // Cleanup DirectLine on unmount
  useEffect(() => {
    return () => {
      teardownDirectLine();
    };
  }, [teardownDirectLine]);

  // ── User cancels while waiting in the DirectLine/InContact queue ───────────
  // Ends the live-agent connection attempt and resets state so the user can
  // return to the AI chat or try "Switch to Live Chat" again later.
  const endLiveAgentChat = useCallback((reason = 'user_exit_queue') => {
    console.log('[DirectLine] User cancelled — sending disconnectLiveAgent cancellation event.');

    const finishTeardown = () => {
      liveModeRef.current = false;
      dlHandoffRef.current = false;
      liveAgentNameRef.current = '';
      setDlHandoff(false);
      setDlFailed(false);
      setDlStatus('');
      setIsLiveMode(false);
      setLiveAgentName('');
      setDlQueueActive(false);
      setDlWaitTime('');
      setDlExiting(false);
      queueContactIdRef.current = null;
      // Release the session-level auto-connect flag so exiting doesn't
      // immediately re-trigger the handoff, and the /question flow (gated on
      // liveAgentConnectivity in useChat.send) works again.
      clearLiveAgentConnectivity();
      // No hardcoded status message — the bot's actual reply (e.g. "Live agent session
      // ended...") already rendered via the existing activity subscription before we get here.
    };

    if (!directLineRef.current) {
      finishTeardown();
      return;
    }

    setDlExiting(true);
    setDlStatus('Disconnecting...');

    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      subscription.unsubscribe();
      clearTimeout(timeoutId);
      // Close DirectLine only after the backend has called InContact's
      // endChatSession() and echoed disconnectLiveAgent back to us.
      finishTeardown();
    };

    // Wait for the backend to acknowledge the cancellation before closing the
    // connection locally — otherwise InContact still delivers the queued/active
    // contact to an agent even though we disconnected on our end.
    const subscription = directLineRef.current.activity$.subscribe(
      (activity) => {
        // DirectLine echoes our own posted activities back through this same
        // stream — ignore that echo and wait for the backend's own disconnectLiveAgent.
        if (activity.type === 'event' && activity.name === 'disconnectLiveAgent' && activity.from?.id !== 'user') {
          settle();
        }
      },
      (error) => {
        console.warn('[DirectLine] exit-queue subscription errored (connection likely already ending):', error);
        settle();
      }
    );

    // Do not close DirectLine without an acknowledgement: doing so only removes
    // the browser from the conversation while InContact can still route its
    // queued contact to an agent. Keep the connection available for a retry.
    const timeoutId = setTimeout(() => {
      if (!settled) {
        subscription.unsubscribe();
        setDlExiting(false);
        setDlStatus('Unable to confirm queue exit. Please try again.');
      }
    }, 10000);

    directLineRef.current.postActivity({
      type: 'event',
      name: 'disconnectLiveAgent',
      value: {
        reason,
        contactId: queueContactIdRef.current,
      },
      from: { id: 'user', role: 'user', name: 'User' },
      channelData: getDirectLineChannelData(userName),
    }).subscribe({
      error: (error) => {
        console.error('[DirectLine] Failed to send disconnectLiveAgent cancellation event:', error);
        // Do not close DirectLine here — keep the connection alive so the
        // cancellation can be retried or a delayed backend response can still arrive.
        settled = true;
        clearTimeout(timeoutId);
        subscription.unsubscribe();
        setDlExiting(false);
        setDlStatus('Failed to exit — please try again.');
      },
    });
  }, [teardownDirectLine, clearLiveAgentConnectivity, userName]);

  const initializeDirectLine = useCallback(async () => {
    if (directLineRef.current || tokenRequestRef.current) return;
    const controller = new AbortController();
    tokenRequestRef.current = controller;
    setLiveAgentDiagnostics(previous => ({ ...previous, connection: 'Requesting token' }));
    setDlFailed(false);

    try {
      const queryParams = new URLSearchParams(window.location.search);
      const session = queryParams.get('session') || sessionStorage.getItem(LOCAL_SESSION_KEY);
      const uid = queryParams.get('uid') || sessionStorage.getItem(LOCAL_UID_KEY);
      if (!session || !uid) throw new Error('Missing session or uid');
      const response = await fetch(`${BACKEND_BASE_URL}/directline/token/${encodeURIComponent(session)}/${encodeURIComponent(uid)}`, {
        method: 'POST',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Token request failed (HTTP ${response.status})`);
      const { token } = await response.json();
      if (controller.signal.aborted) return;
      // SECURITY_NOTE: never log the raw token value, only whether one was received.
      console.log('[DirectLine] token received:', token ? `yes (length ${token.length})` : 'NO TOKEN RETURNED');
      if (!token) throw new Error('No DirectLine token returned by backend');

      const dl = new DirectLine({ token }); // SDK opens WebSocket automatically
      directLineRef.current = dl;
      setLiveAgentDiagnostics(previous => ({ ...previous, connection: 'Connecting' }));
      console.log('[DirectLine] DirectLine instance created, opening WebSocket…');

      // Tracks the most recent real agent chat text (from the 'agentmessages'
      // event), so the bot-relayed plain-text copy of that same message can be
      // told apart from genuine bot notices (e.g. "Live agent session ended").
      let lastAgentMessageText = '';

      subscriptionRef.current = dl.activity$.subscribe(async activity => {

        logAgentAvailability(activity);
        if (activity.conversation?.id) inContactSessionIdRef.current = activity.conversation.id;

        // ── Log every incoming activity for debugging ──────────────────────
        console.log('[DirectLine] activity received:', JSON.stringify({
          type: activity.type,
          name: activity.name,
          from: activity.from,
          text: activity.text,
          value: activity.value,
          attachments: activity.attachments,
        }, null, 2));

        // Ignore replies to questions that were aborted or resent.
        if (activity.replyToId && abortedPostIdsRef.current.has(activity.replyToId)) return;

        // Backend failed to answer: show its error text with a resend button.
        if (activity.type === 'event' && activity.name === 'aiResponseError' && !liveModeRef.current) {
          abortPendingAiRequest();
          directLineLoadingRef.current = false;
          setDirectLineLoading(false);
          setDirectLineStreamingEvents([]);
          setDlMessages(prev => [...prev, {
            role: 'status',
            content: activity.value?.error || '',
            retryText: lastQuestionRef.current,
            timestamp: Date.now(),
          }]);
          return;
        }

        // Bot reports the live-agent handoff failed — show a generic message with Try Again instead of its raw text.
        if (activity.type === 'message' && activity.from?.id !== 'user' && activity.from?.role !== 'user'
          && typeof activity.text === 'string' && activity.text.toLowerCase().includes('unable to connect to live agent')) {
          console.warn('[DirectLine] Bot reported live-agent connection failure:', activity.text);
          directLineLoadingRef.current = false;
          setDirectLineLoading(false);
          setDirectLineStreamingEvents([]);
          setDlStatus(LIVE_AGENT_FAILURE_MESSAGE);
          setDlFailed(true);
          setDlQueueActive(false);
          setDlMessages(prev => [...prev, {
            role: 'status',
            content: LIVE_AGENT_FAILURE_MESSAGE,
            retryLiveAgent: true,
            timestamp: Date.now(),
          }]);
          return;
        }

        if (activity.type === 'event'
          && ['agentEWT', 'agentjoined', 'agentmessages', 'disconnectLiveAgent', 'agentsNotAvailable'].includes(activity.name)
          && activity.from?.id !== 'user' && activity.from?.role !== 'user') {
          responsePlaybackRef.current?.abort();
          directLineLoadingRef.current = false;
          setDirectLineLoading(false);
          setDirectLineStreamingEvents([]);
        }

        const isAiMetadata = activity.type === 'event' && activity.name === 'aiResponseMetadata';
        const aiResponse = !liveModeRef.current && parseDirectLineAiResponse(activity);
        if (aiResponse) {
          if (isAiMetadata && activity.value?.session_ended === true) {
            const params = new URLSearchParams(window.location.search);
            const session = params.get('session') || sessionStorage.getItem(LOCAL_SESSION_KEY);
            const uid = params.get('uid') || sessionStorage.getItem(LOCAL_UID_KEY);
            if (session && uid) notifySessionTimeout(session, uid, userNameRef.current);
          }
          responsePlaybackRef.current?.abort();
          const playback = new AbortController();
          responsePlaybackRef.current = playback;
          directLineLoadingRef.current = true;
          setDirectLineLoading(true);
          let previousTime = 0;
          for (const event of aiResponse.streamingEvents || []) {
            if (playback.signal.aborted || liveModeRef.current) break;
            if (typeof event?.message !== 'string') continue;
            setDirectLineStreamingEvents([event]);
            const delay = Math.min(Math.max(0, (event.timestamp_ms ?? previousTime) - previousTime) * 0.35, 700);
            await new Promise(resolve => {
              const finish = () => {
                clearTimeout(timer);
                playback.signal.removeEventListener('abort', finish);
                resolve();
              };
              const timer = setTimeout(finish, Math.max(delay, 150));
              playback.signal.addEventListener('abort', finish, { once: true });
            });
            previousTime = event.timestamp_ms ?? previousTime;
          }
          if (playback.signal.aborted || liveModeRef.current) return;
          const content = aiResponse.content || aiResponse.sessionEndMessage;
          const proactive = isAiMetadata && typeof activity.value?.proactive_message === 'string'
            ? activity.value.proactive_message : aiResponse.proactiveMessage || '';
          if (isAiMetadata) {
            const bufferedReplies = pendingAiReplyRef.current;
            setDlMessages(previous => {
              const withReplies = bufferedReplies.reduce((history, text) => appendAiReply(history, {
                role: 'assistant',
                content: text,
                agentType: 'ai',
                timestamp: Date.now(),
              }), previous);
              return appendAiReply(withReplies, {
                role: 'assistant',
                content: '',
                proactiveMessage: proactive,
                agentType: 'ai',
                timestamp: Date.now(),
              });
            });
          } else if (content || proactive || aiResponse.orderCard) {
            aiReplyReceivedRef.current = true;
            setDlMessages(previous => appendAiReply(previous, {
              role: 'assistant',
              content: content || '',
              orderCard: aiResponse.orderCard,
              proactiveMessage: proactive,
              tableOrderNumber: aiResponse.tableOrderNumber || '',
              intent: aiResponse.intent || '',
              agentType: 'ai',
              timestamp: Date.now(),
            }));
          }
          pendingAiReplyRef.current = [];
          metadataReplayCompleteRef.current = true;
          responsePlaybackRef.current = null;
          const waitingForAnswer = isAiMetadata && !aiReplyReceivedRef.current;
          directLineLoadingRef.current = waitingForAnswer;
          setDirectLineLoading(waitingForAnswer);
          setDirectLineStreamingEvents([]);
          if (isAiMetadata && activity.value?.session_ended === true) {
            dispatch(setSessionEnded(true));
          }
          metadataEscalationRef.current = aiResponse.escalationRequired;
          if (aiResponse.escalationRequired && (!isAiMetadata || aiReplyReceivedRef.current)) {
            metadataEscalationRef.current = false;
            dispatch(setEscalationRequired(true));
          }
          return;
        }

        const streamEventNames = ['streaming_event', 'streaming_events', 'streaming_progress'];
        if (activity.type === 'event' && streamEventNames.includes(activity.name)) {
          const payload = activity.value?.streaming_events || activity.value?.event || activity.value;
          const events = (Array.isArray(payload) ? payload : [payload]).filter(event =>
            typeof event?.message === 'string' && ['planning', 'node_executed', 'complete'].includes(event.phase));
          if (events.length && directLineLoadingRef.current && !liveModeRef.current) {
            setDirectLineLoading(true);
            setDirectLineStreamingEvents(events);
          }
          return;
        }

        // ── EVENT: No live agents currently available ──────────────────────
        if (activity.type === 'event' && activity.name === 'agentsNotAvailable') {
          const { availableAgents } = activity.value || {};
          console.log('[DirectLine] agentsNotAvailable — availableAgents:', availableAgents, 'activity.name:', activity.name);
          setDlQueueActive(false);
          setDlStatus('No live agents are available right now.');
          setDlFailed(true);
          const notice = 'No live agents are available right now. Please try again later.';
          if (shouldDisplayEventMessage(notice)) {
            setDlMessages(prev => [...prev.filter(m => !m.transferNotice), {
              role: 'status',
              content: notice,
              timestamp: Date.now(),
            }]);
          }
        }

        // ── EVENT: Waiting in queue — estimated wait time update ───────────
        if (activity.type === 'event' && activity.name === 'agentEWT') {
          const { contactId, availableAgents, estimatedWaitTime } = activity.value || {};
          console.log('[DirectLine] agentEWT — contactId:', contactId, 'availableAgents:', availableAgents, 'estimatedWaitTime:', estimatedWaitTime, 'activity.name:', activity.name);
          queueContactIdRef.current = contactId || null;
          setDlStatus('Waiting in queue for a live agent...');
          setDlQueueActive(true);
          setDlMessages(prev => prev.filter(m => !m.transferNotice));
          // Backend doesn't always have a numeric estimate yet (sends null) — fall
          // back to a fixed estimate so the QueueCard still shows something.
          setDlWaitTime(
            estimatedWaitTime !== null && estimatedWaitTime !== undefined
              ? formatWaitTime(Number(estimatedWaitTime))
              : `${Math.floor(Math.random() * 4) + 1} mins`
          );
        }

        // ── InContact agent message (event, attachment or direct message) ─
        // Backend emits: { type: 'event', name: 'agentmessages',
        //   value: { label: 'Agent name', message: 'Agent text' } }.
        const agentMessage = getLiveAgentMessage(activity, liveAgentNameRef.current || 'Live agent');
        const displayAgentMessage = agentMessage && shouldDisplayEventMessage(agentMessage.text);
        if (activity.name === 'agentmessages') {
          const value = activity.value;
          const rejectionReason = agentMessage ? null
            : activity.type !== 'event' ? 'Expected activity.type to be event'
            : activity.from?.id === 'user' || activity.from?.role === 'user' ? 'Ignored user echo'
            : typeof value?.label === 'string' && value.label.toLowerCase() === 'user' ? 'Ignored user label'
            : typeof value?.message !== 'string' ? 'value.message must be a string'
            : 'value.message is empty';
          const diagnostics = {
            conversationId: activity.conversation?.id || dl.conversationId,
            activityId: activity.id,
            timestamp: activity.timestamp,
            receivedAt: new Date().toISOString(),
            type: activity.type,
            name: activity.name,
            senderRole: activity.from?.role,
            valueType: Array.isArray(value) ? 'array' : value === null ? 'null' : typeof value,
            valueKeys: value && typeof value === 'object' ? Object.keys(value) : [],
            messageType: typeof value?.message,
            messageLength: typeof value?.message === 'string' ? value.message.length : 0,
            acceptedForDisplay: Boolean(displayAgentMessage),
            rejectionReason,
          };
          // SECURITY_NOTE: correlation metadata only; do not log tokens or message content here.
          if (displayAgentMessage) {
            console.info('[DirectLine] agentmessages received and accepted for display:', diagnostics);
          } else if (agentMessage) {
            console.info('[DirectLine] duplicate agentmessages suppressed:', diagnostics);
          } else {
            console.warn('[DirectLine] agentmessages received but rejected:', diagnostics);
          }
          setLiveAgentDiagnostics(previous => ({
            ...previous,
            accepted: previous.accepted + (displayAgentMessage ? 1 : 0),
            rejected: previous.rejected + (agentMessage ? 0 : 1),
            lastEvent: {
              receivedAt: new Date().toLocaleTimeString(),
              type: activity.type || 'missing',
              sender: activity.from?.role || activity.from?.id || 'not provided',
              activityId: activity.id || 'not provided',
              result: !agentMessage
                ? `Rejected: ${rejectionReason}`
                : displayAgentMessage ? 'Accepted and appended to UI' : 'Duplicate suppressed',
            },
          }));
        }
        // Live agent sent a file — show it as a download link in the chat.
        const agentAttachment = activity.from?.role !== 'user' && activity.from?.id !== 'user'
          ? getAgentAttachment(activity) : null;
        if (agentAttachment) {
          if (liveModeRef.current) {
            setDlMessages(prev => [...prev, {
              role: 'assistant',
              content: '',
              attachment: agentAttachment,
              agentName: agentAttachment.agentName || liveAgentNameRef.current || 'Live agent',
              agentType: 'live',
              timestamp: Date.now(),
            }]);
          } else {
            console.warn('[DirectLine] Ignored agent attachment received outside a live-agent session.');
          }
          return;
        }

        if (agentMessage) {
          liveModeRef.current = true;
          setIsLiveMode(true);
          const { text, agentName } = agentMessage;
          lastAgentMessageText = text;
          dlHandoffRef.current = true;
          setDlHandoff(true);
          setDlQueueActive(false);
          liveAgentNameRef.current = agentName;
          setLiveAgentName(agentName);
          setDlStatus(`Connected to ${agentName}`);
          if (displayAgentMessage) {
            setDlMessages(prev => [...prev, {
              role: 'assistant',
              content: text,
              agentName,
              agentType: 'live',
              timestamp: Date.now(),
            }]);
          }
        } else if (activity.type === 'message' && activity.from?.id !== 'user' && activity.from?.role !== 'user' && activity.from?.role !== 'agent' && activity.from?.id !== 'incontact') {
          // Plain bot dialog message. Once the live-agent handoff has
          // actually completed (dlHandoffRef.current), attribute it to the
          // live agent. Before that, it's a progress/status update from the
          // bot (e.g. "Connecting you to a live agent, please wait...",
          // "You've been placed in the queue...") — show it as a status
          // line rather than suppressing it, except for the known fallback
          // error text which is handled separately below.
          const isFallbackError = activity.text?.includes("I couldn't process your request right now");
          // The bot's "connecting" status duplicates the message already shown the
          // instant connectToLiveAgent() started — skip re-adding it to the chat.
          const isConnectingMessage = activity.text?.includes('Connecting you to a live agent, please wait');
          // Queue-placement message is redundant with the QueueCard shown for
          // the same event — suppress it too.
          const isQueuedMessage = activity.text?.includes("You've been placed in the queue for a live agent");
          if (!liveModeRef.current && typeof activity.text === 'string' && activity.text.trim()) {
            aiReplyReceivedRef.current = true;
            if ((directLineLoadingRef.current && !metadataReplayCompleteRef.current)
              || (responsePlaybackRef.current && !responsePlaybackRef.current.signal.aborted)) {
              pendingAiReplyRef.current.push(activity.text);
              return;
            }
            directLineLoadingRef.current = false;
            setDirectLineLoading(false);
            setDirectLineStreamingEvents([]);
            const isQueueNotice = activity.text.toLowerCase().includes('placed in the queue');
            if (!isQueueNotice || shouldDisplayEventMessage(activity.text)) {
              setDlMessages(prev => appendAiReply(prev, {
                role: 'assistant',
                content: activity.text,
                agentType: 'ai',
                timestamp: Date.now(),
              }));
            }
            if (metadataEscalationRef.current) {
              metadataEscalationRef.current = false;
              dispatch(setEscalationRequired(true));
            }
          } else if (dlHandoffRef.current) {
            // Real agent chat text already arrives via the 'agentmessages'
            // event (or from.role === 'agent'); only skip this bot-relayed
            // copy when it duplicates that text — genuine bot notices (e.g.
            // "Live agent session ended...") have no such event and must
            // still be shown.
            const isDuplicateAgentText = lastAgentMessageText && activity.text?.includes(lastAgentMessageText);
            if (!isDuplicateAgentText && activity.text) {
              setDlMessages(prev => [...prev, {
                role: 'status',
                content: activity.text,
                timestamp: Date.now(),
              }]);
            }
          } else if (activity.text && !isFallbackError && !isConnectingMessage && !isQueuedMessage) {
            console.log('[DirectLine] pre-handoff status message from bot:', activity.text, 'activity.name:', activity.name);
            setDlStatus(activity.text);
            setDlMessages(prev => [...prev, {
              role: 'status',
              content: activity.text,
              timestamp: Date.now(),
            }]);
          }

          // Bot fallback reply — the "human" trigger phrase wasn't
          // recognized/handled by the bot dialog. Surface that instead of
          // leaving the banner stuck on "Connecting..." forever.
          if (liveModeRef.current && !dlHandoffRef.current && isFallbackError) {
            // Log full activity context (conversation/activity ids, timestamp,
            // channelData) so backend/App Insights logs for this exchange can
            // be correlated when diagnosing why the bot dialog fell back.
            console.warn(
              '[DirectLine] Bot returned a fallback/error message instead of a live-agent handoff event — the "human" trigger was not recognized by the bot dialog.',
              {
                activityId: activity.id,
                activityName: activity.name,
                conversationId: activity.conversation?.id,
                timestamp: activity.timestamp,
                from: activity.from,
                text: activity.text,
                channelData: activity.channelData,
              }
            );
            setDlStatus(LIVE_AGENT_FAILURE_MESSAGE);
            setDlFailed(true);
            setDlMessages(prev => [...prev, {
              role: 'status',
              content: LIVE_AGENT_FAILURE_MESSAGE,
              retryLiveAgent: true,
              timestamp: Date.now(),
            }]);
          }
        }

        // ── EVENT: Agent accepted — handoff is live ────────────────────────
        if (activity.type === 'event' && activity.name === 'agentjoined') {
          liveModeRef.current = true;
          setIsLiveMode(true);
          const agentName = getLiveAgentName(activity.value);
          console.log('[DirectLine] agentjoined — agent:', agentName, 'activity.name:', activity.name);
          setDlHandoff(true);
          dlHandoffRef.current = true;
          liveAgentNameRef.current = agentName;
          setLiveAgentName(agentName);
          setDlStatus(`Connected to ${agentName}`);
          setDlQueueActive(false);
          const notice = 'A live agent has joined the chat.';
          if (shouldDisplayEventMessage(notice)) {
            setDlMessages(prev => [...prev, {
              role: 'status',
              content: notice,
              timestamp: Date.now(),
            }]);
          }
        }

        // ── EVENT: Chat transferred to another agent ───────────────────────
        if (activity.type === 'event' && activity.name === 'agenttransfered') {
          const { contactId } = activity.value || {};
          const agentName = getLiveAgentName(activity.value);
          console.log('[DirectLine] agenttransfered — agent:', agentName, 'contactId:', contactId, 'activity.name:', activity.name);
          setDlHandoff(true);
          dlHandoffRef.current = true;
          liveAgentNameRef.current = agentName;
          setLiveAgentName(agentName);
          setDlStatus(`Transferred to ${agentName}`);
          setDlQueueActive(false);
          const notice = `Your chat has been transferred to ${agentName}.`;
          if (shouldDisplayEventMessage(notice)) {
            setDlMessages(prev => [...prev, {
              role: 'status',
              content: notice,
              timestamp: Date.now(),
            }]);
          }
        }

        // ── EVENT: Backend/agent ended the live-agent session ──────────────
        if (activity.type === 'event' && activity.name === 'disconnectLiveAgent'
          && activity.from?.id !== 'user' && activity.from?.role !== 'user') {
          liveModeRef.current = false;
          console.log('[DirectLine] disconnectLiveAgent received — activity.name:', activity.name);
          dlHandoffRef.current = false;
          liveAgentNameRef.current = '';
          setDlHandoff(false);
          setDlQueueActive(false);
          setDlWaitTime('');
          queueContactIdRef.current = null;
          setLiveAgentName('');
          setIsLiveMode(false);
          clearLiveAgentConnectivity();
          const notice = 'The live agent has disconnected.';
          if (shouldDisplayEventMessage(notice)) {
            setDlMessages(prev => [...prev, {
              role: 'status',
              content: notice,
              timestamp: Date.now(),
            }]);
          }
        }

      },
      // Ending the connection makes this observable error with "conversation ended" —
      // without a handler RxJS rethrows it asynchronously as an uncaught exception.
      (error) => {
        console.warn('[DirectLine] activity$ subscription errored (connection likely already ending):', error);
      }
      );

      connectionStatusSubRef.current = dl.connectionStatus$.subscribe(status => {
        console.log('[DirectLine] connectionStatus changed:', status, '=', ConnectionStatus[status]);
        setDlOnline(status === ConnectionStatus.Online);
        setLiveAgentDiagnostics(previous => ({
          ...previous,
          connection: ConnectionStatus[status] || `Status ${status}`,
        }));

        if (status === ConnectionStatus.FailedToConnect || status === ConnectionStatus.Ended) {
          responsePlaybackRef.current?.abort();
          directLineLoadingRef.current = false;
          setDirectLineLoading(false);
          setDirectLineStreamingEvents([]);
          console.error('[DirectLine] connection failed, status:', status, '=', ConnectionStatus[status]);
          setDlStatus(LIVE_AGENT_FAILURE_MESSAGE);
          setDlFailed(true);
          setDlQueueActive(false);
          setDlMessages(prev => [...prev, {
            role: 'status',
            content: LIVE_AGENT_FAILURE_MESSAGE,
            retryLiveAgent: true,
            timestamp: Date.now(),
          }]);
        }
      },
      // connectionStatus$ can also error with "conversation ended" once .end() is called.
      (error) => {
        console.warn('[DirectLine] connectionStatus$ subscription errored (connection likely already ending):', error);
      }
      );

    } catch (err) {
      if (controller.signal.aborted) return;
      console.error('[DirectLine] connectToLiveAgent() threw — connection setup failed:', err);
      setLiveAgentDiagnostics(previous => ({ ...previous, connection: 'Connection setup failed' }));
      setDlStatus(LIVE_AGENT_FAILURE_MESSAGE);
      setDlFailed(true);
      setDlQueueActive(false);
      setDlMessages(prev => [...prev, {
        role: 'status',
        content: LIVE_AGENT_FAILURE_MESSAGE,
        retryLiveAgent: true,
        timestamp: Date.now(),
      }]);
    } finally {
      if (tokenRequestRef.current === controller) tokenRequestRef.current = null;
    }
  }, [dispatch]);

  useEffect(() => {
    if (!orderLoading) initializeDirectLine();
  }, [orderLoading, initializeDirectLine]);

  // Re-open the DirectLine connection after a live-agent failure (also used by the header button).
  const retryLiveAgent = useCallback(() => {
    teardownDirectLine();
    liveModeRef.current = false;
    setIsLiveMode(false);
    initializeDirectLine();
  }, [teardownDirectLine, initializeDirectLine]);

  const connectToLiveAgent = useCallback(() => {
    if (!dlOnline || userNameLoading || !directLineRef.current || liveModeRef.current) return;
    liveModeRef.current = true;
    setIsLiveMode(true);
    setDlFailed(false);
    responsePlaybackRef.current?.abort();
    directLineLoadingRef.current = false;
    setDirectLineLoading(false);
    setDirectLineStreamingEvents([]);
    setDlMessages(previous => [...previous, {
      role: 'status',
      content: 'You are being transferred to a live chat agent.',
      transferNotice: true,
      timestamp: Date.now(),
    }]);
    directLineRef.current.postActivity({
      type: 'message',
      from: { id: 'user', role: 'user' },
      text: 'escalatedToAgent',
      channelData: getDirectLineChannelData(userName),
    }).subscribe({
      error: () => {
        liveModeRef.current = false;
        setIsLiveMode(false);
        setDlFailed(true);
        setDlMessages(previous => [...previous, {
          role: 'status',
          content: LIVE_AGENT_FAILURE_MESSAGE,
          retryLiveAgent: true,
          timestamp: Date.now(),
        }]);
      },
    });
  }, [dlOnline, userNameLoading, userName]);

  // ── Auto-trigger DirectLine live-agent handoff when the backend reports ────
  // the session already has live_agent_connectivity (e.g. re-opened chat that
  // was already handed off), instead of waiting for the user to click the
  // "Switch to Live Chat" button.
  // Guarded by a one-shot ref (not re-armed): live_agent_connectivity is a
  // session-level flag that never resets by itself, so without this guard
  // exiting the chat (which clears isLiveMode) would immediately re-trigger
  // the same handoff instead of letting the user fall back to the AI agent.
  const connectivityAutoConnectedRef = useRef(false);
  useEffect(() => {
    if (dlOnline && !userNameLoading && liveAgentConnectivity && !isLiveMode && !connectivityAutoConnectedRef.current) {
      connectivityAutoConnectedRef.current = true;
      connectToLiveAgent();
    }
  }, [dlOnline, userNameLoading, liveAgentConnectivity, isLiveMode, connectToLiveAgent]);

  // ── Auto-trigger DirectLine live-agent handoff when a /question response ───
  // reports escalation_required === true mid-conversation. Guarded by
  // isLiveMode (not a one-shot ref) so a later escalation_required response —
  // e.g. after the user exits a previous live-agent chat — can re-trigger the
  // handoff instead of being silently ignored.
  useEffect(() => {
    if (dlOnline && !userNameLoading && escalationRequired && !isLiveMode) {
      clearEscalation();
      connectToLiveAgent();
    }
  }, [dlOnline, userNameLoading, escalationRequired, isLiveMode, clearEscalation, connectToLiveAgent]);

  // ── Send message over DirectLine ───────────────────────────────────────────
  // Stops the pending AI request: its timer, post subscription and reply playback.
  const abortPendingAiRequest = useCallback(() => {
    postSubscriptionRef.current?.unsubscribe();
    postSubscriptionRef.current = null;
    if (activePostIdRef.current) abortedPostIdsRef.current.add(activePostIdRef.current);
    activePostIdRef.current = null;
    responsePlaybackRef.current?.abort();
    responsePlaybackRef.current = null;
  }, []);

  const sendDlMessage = useCallback((text, { showUserMessage = true } = {}) => {
    if (!text.trim() || !dlOnline || userNameLoading || !directLineRef.current || directLineLoadingRef.current) return;
    const expectsAiResponse = !liveModeRef.current && !dlHandoffRef.current;
    abortPendingAiRequest();
    if (expectsAiResponse) {
      pendingAiReplyRef.current = [];
      metadataReplayCompleteRef.current = false;
      aiReplyReceivedRef.current = false;
      metadataEscalationRef.current = false;
      lastQuestionRef.current = text;
      directLineLoadingRef.current = true;
      setDirectLineLoading(true);
      setDirectLineStreamingEvents([]);
    }

    postSubscriptionRef.current = directLineRef.current.postActivity({
      type: 'message',
      from: { id: 'user', role: 'user' },
      text,
      channelData: getDirectLineChannelData(userName),
    }).subscribe(
      id  => {
        activePostIdRef.current = id;
        console.log('[DirectLine] message sent, id:', id);
      },
      (error) => {
        directLineLoadingRef.current = false;
        setDirectLineLoading(false);
        setDirectLineStreamingEvents([]);
        setDlMessages(previous => [...previous, {
          role: 'status',
          content: error?.message || '',
          retryText: text,
          timestamp: Date.now(),
        }]);
      }
    );

    if (showUserMessage) {
      setDlMessages(prev => [...prev, {
        role: 'user',
        content: text,
        timestamp: Date.now(),
      }]);
    }
  }, [dlOnline, userNameLoading, userName, abortPendingAiRequest]);

  const handleSend = useCallback((text) => {
    sendDlMessage(text);
  }, [sendDlMessage]);

  // Live-agent idle: after 10 min without input, confirm; no reply in 30 s exits the chat.
  const endLiveAgentChatRef = useRef(endLiveAgentChat);
  endLiveAgentChatRef.current = endLiveAgentChat;

  useEffect(() => {
    if (!isLiveMode || !dlHandoff) return undefined;
    let prompting = false;
    let idleTimer;
    let confirmTimer;

    const startIdle = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        prompting = true;
        setLiveIdlePrompt(true);
        confirmTimer = setTimeout(() => {
          prompting = false;
          setLiveIdlePrompt(false);
          endLiveAgentChatRef.current('idle_timeout');
        }, LIVE_IDLE_CONFIRM_MS);
      }, LIVE_IDLE_TIMEOUT_MS);
    };
    const onActivity = () => {
      if (!prompting) startIdle();
    };

    LIVE_ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, onActivity, { passive: true }));
    startIdle();

    return () => {
      LIVE_ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, onActivity));
      clearTimeout(idleTimer);
      clearTimeout(confirmTimer);
      setLiveIdlePrompt(false);
    };
  }, [isLiveMode, dlHandoff, liveIdleResetKey]);

  // User confirmed they are still there — restart the 10-minute idle window.
  const continueLiveChat = useCallback(() => {
    setLiveIdlePrompt(false);
    setLiveIdleResetKey(key => key + 1);
  }, []);

  // Attachments are only accepted while a live agent is connected.
  const canAttachFile = isLiveMode && dlHandoff && dlOnline && !dlExiting && !sessionTimedOut;

  const handleAttachFile = useCallback(async (file) => {
    const CaseID = order?.CaseID;
    if (!CaseID || !dlHandoffRef.current) return;

    setAttachmentUploading(true);
    try {
      await uploadAttachment({ CaseID, file, inContactSessionID: inContactSessionIdRef.current });
      setDlMessages(prev => [...prev, { role: 'user', content: `Attached: ${file.name}`, timestamp: Date.now() }]);
    } catch (err) {
      console.error('[Attachment] upload failed:', err);
      setDlMessages(prev => [...prev, { role: 'status', content: 'Unable to upload the file. Please try again.', timestamp: Date.now() }]);
    } finally {
      setAttachmentUploading(false);
    }
  }, [order]);

  const handleRetry = useCallback((text) => {
    sendDlMessage(text, { showUserMessage: false });
  }, [sendDlMessage]);

  return {
    dlMessages,
    directLineLoading,
    isLiveMode,
    dlOnline,
    dlHandoff,
    dlFailed,
    dlQueueActive,
    dlWaitTime,
    dlExiting,
    attachmentUploading,
    liveAgentDiagnostics,
    canAttachFile,
    handleSend,
    handleRetry,
    handleAttachFile,
    retryLiveAgent,
    connectToLiveAgent,
    endLiveAgentChat,
    liveIdlePrompt,
    continueLiveChat,
  };
}
